// src/pages/GuestListPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { DetailList, PageHeader, RowActions, Section, StatusTag, tablePagination, useFlatPage } from "../components/ui";
import "./GuestListPage.css";
import {
  Alert,
  Button,
  Col,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography,
} from "antd";
import {
  FullscreenOutlined,
  FullscreenExitOutlined,
} from "@ant-design/icons";
import { IconDoc, IconInfo, IconRefresh, IconSearch, IconView } from "../components/ui/icons";
import notify from "../utils/notify";

const { Text } = Typography;
const { Option } = Select;

/* ---------- slim design ---------- */
/* ---------- helpers ---------- */
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const H = ({ children }) => <span style={{ whiteSpace: "nowrap" }}>{children}</span>;

const money = (amt, cur = "BWP") => {
  if (amt == null) return "—";
  const n = Number(amt);
  if (!Number.isFinite(n)) return `${cur}${amt}`;
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format(n);
  } catch {
    return `${cur}${n.toFixed(2)}`;
  }
};

const pick = (o, ...keys) => {
  for (const k of keys) {
    const v = o?.[k];
    if (v != null && String(v).trim() !== "") return v;
  }
  return null;
};

const GuestListPage = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  useFlatPage();

  // core data
  const [guests, setGuests] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);

  // ui state
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // drawer state (mode-based)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("details"); // "details" | "history"
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerFull, setDrawerFull] = useState(false);

  const [selectedGuest, setSelectedGuest] = useState(null);
  const [bookings, setBookings] = useState([]);

  // edit modal
  const [isEditGuestModalOpen, setIsEditGuestModalOpen] = useState(false);

  // responsive check
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);


  const fetchGuests = async (hotelId) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/Guests/hotel/${hotelId}`, {
        params: { page: currentPage, pageSize: itemsPerPage },
      });

      const list = res.data.guests || res.data.Guests || [];
      const total = res.data.totalCount ?? res.data.TotalCount ?? list.length;

      setGuests(list);
      setTotalEntries(total);
      setError(null);
    } catch {
      setError("Failed to load guests.");
      notify.error({ message: "Error", description: "Failed to load guests." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authData?.hotelId) {
      fetchGuests(authData.hotelId);
    } else {
      setError("User authentication is missing. Please log in.");
      navigate("/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.hotelId, currentPage, itemsPerPage]);

  const filteredGuests = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return guests;
    return guests.filter((g) =>
      `${g.firstName ?? ""} ${g.lastName ?? ""} ${g.email ?? ""} ${g.idNumber ?? ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [guests, searchQuery]);

  // --- fetch full guest details (for upgraded details view) ---
  const fetchGuestDetails = async (guestId) => {
    if (!guestId) return null;
    try {
      const res = await axiosInstance.get(`/Guests/${guestId}`, {
        params: { hotelId: authData?.hotelId },
      });
      return res.data || null;
    } catch {
      return null;
    }
  };

  const fetchBookingHistory = async (guest) => {
    const idNumber = pick(guest, "idNumber", "IdNumber");
    if (!idNumber) return [];
    const res = await axiosInstance.get(`/Bookings/SearchBookingsByGuest`, {
      params: { idNumber, hotelId: authData.hotelId },
    });
    return res.data || [];
  };

  // --- open drawers (separated actions) ---
  const openGuestDetails = async (guest) => {
    setSelectedGuest(guest);
    setBookings([]);
    setDrawerMode("details");
    setDrawerOpen(true);
    setDrawerFull(false);

    setDrawerLoading(true);
    try {
      const full = await fetchGuestDetails(guest.guestId ?? guest.GuestId);
      if (full) setSelectedGuest((prev) => ({ ...(prev || {}), ...(full || {}) }));
    } finally {
      setDrawerLoading(false);
    }
  };

  const openBookingHistory = async (guest) => {
    setSelectedGuest(guest);
    setBookings([]);
    setDrawerMode("history");
    setDrawerOpen(true);
    setDrawerFull(false);

    setDrawerLoading(true);
    try {
      // parallel: full guest + bookings
      const [full, hist] = await Promise.all([
        fetchGuestDetails(guest.guestId ?? guest.GuestId),
        fetchBookingHistory(guest),
      ]);
      if (full) setSelectedGuest((prev) => ({ ...(prev || {}), ...(full || {}) }));
      setBookings(hist || []);
    } catch (err) {
      notify.info({
        message: "No booking history",
        description:
          err?.response?.status === 404
            ? "No bookings found for this guest."
            : "Could not load booking history.",
      });
      setBookings([]);
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleGuestDeleted = async (guest) => {
    try {
      await axiosInstance.delete(`/Guests/${guest.guestId}`);
      notify.success({ message: "Guest deleted" });
      fetchGuests(authData.hotelId);
    } catch {
      notify.error({ message: "Failed to delete guest" });
    }
  };

  /* ---------- table columns ---------- */

  const guestColumns = [
    {
      title: "Guest",
      dataIndex: "firstName",
      width: 240,
      render: (_, r) => (
        <div className="gl-guest">
          <span className="gl-name">{`${r.firstName ?? ""} ${r.lastName ?? ""}`.trim() || "—"}</span>
          <span className="gl-sub">
            #{r.guestId} · {pick(r, "idNumber", "IdNumber") || "no ID number"}
          </span>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      width: 220,
      render: (t) => <span className="gl-text">{t || "—"}</span>,
      responsive: ["md"],
    },
    {
      title: "Phone",
      dataIndex: "mobileNumber",
      width: 150,
      render: (_, r) => (
        <span className="gl-text">{pick(r, "mobileNumber", "phoneNumber", "MobileNumber", "PhoneNumber") || "—"}</span>
      ),
      responsive: ["lg"],
    },
    {
      title: "Address",
      dataIndex: "address",
      width: 220,
      render: (_, r) => {
        const addr =
          pick(r, "postalAddress", "PostalAddress") ||
          pick(r, "residentialAddress", "ResidentialAddress") ||
          pick(r, "address", "Address");
        return (
          <span className="gl-text gl-ellipsis" title={addr || ""}>
            {addr || "—"}
          </span>
        );
      },
      responsive: ["xl"],
    },
    {
      title: "Registered",
      dataIndex: "arrivalDate",
      width: 120,
      render: (_, r) => fmtDate(pick(r, "arrivalDate", "ArrivalDate", "createdAt", "CreatedAt")),
      sorter: (a, b) =>
        new Date(pick(a, "arrivalDate", "ArrivalDate", "createdAt", "CreatedAt") || 0).getTime() -
        new Date(pick(b, "arrivalDate", "ArrivalDate", "createdAt", "CreatedAt") || 0).getTime(),
    },
    {
      title: "",
      fixed: "right",
      width: 150,
      align: "right",
      render: (_, record) => (
        <RowActions
          actions={[
            { key: "details", label: "Guest details", icon: "view", onClick: () => openGuestDetails(record) },
            { key: "history", label: "Booking history", icon: "history", onClick: () => openBookingHistory(record) },
            {
              key: "edit",
              label: "Edit guest",
              icon: "edit",
              disabled: true,
              disabledReason: "Editing a guest isn't available yet",
              onClick: () => {
                setSelectedGuest(record);
                setIsEditGuestModalOpen(true);
              },
            },
            {
              key: "delete",
              label: "Delete guest",
              icon: "delete",
              danger: true,
              confirm: "Delete this guest?",
              okText: "Delete",
              onClick: () => handleGuestDeleted(record),
            },
          ]}
        />
      ),
    },
  ];

  const bookingColumns = [
    {
      title: <H>Booking #</H>,
      dataIndex: "bookingNo",
      width: 120,
      ellipsis: true,
      render: (t, r) => {
        const id = r.bookingId ?? t;
        const label = t || id || "—";
        return id ? (
          <a href={`/bookings/${id}`} onClick={(e) => e.stopPropagation()}>
            {label}
          </a>
        ) : (
          label
        );
      },
    },
    { title: <H>Room</H>, width: 80, render: (_, r) => r.room?.roomNumber || r.roomNumber || "—" },
    { title: <H>Check-In</H>, dataIndex: "checkInDate", width: 110, render: fmtDate },
    { title: <H>Check-Out</H>, dataIndex: "checkOutDate", width: 110, render: fmtDate },
    {
      title: <H>Nights</H>,
      width: 70,
      align: "right",
      render: (_, r) => {
        const a = r.checkInDate ? new Date(r.checkInDate) : null;
        const b = r.checkOutDate ? new Date(r.checkOutDate) : null;
        if (!a || !b) return "—";
        return Math.max(Math.ceil((b - a) / 86400000), 1);
      },
    },
    {
      title: <H>Status</H>,
      dataIndex: "status",
      width: 100,
      render: (s) => <StatusTag status={s}>{s || "—"}</StatusTag>,
    },
    {
      title: <H>Total</H>,
      align: "right",
      width: 110,
      render: (_, r) => money(r.totalAmount ?? r.TotalAmount, r.currency ?? r.Currency ?? "BWP"),
    },
    {
      title: <H>Paid</H>,
      align: "right",
      width: 110,
      render: (_, r) => money(r.paidAmount ?? r.PaidAmount ?? 0, r.currency ?? r.Currency ?? "BWP"),
    },
    {
      title: <H>Balance</H>,
      align: "right",
      width: 110,
      render: (_, r) => {
        const total = Number(r.totalAmount ?? r.TotalAmount ?? 0);
        const paid = Number(r.paidAmount ?? r.PaidAmount ?? 0);
        const cur = r.currency ?? r.Currency ?? "BWP";
        const bal = Number.isFinite(total - paid) ? Math.max(total - paid, 0) : 0;
        return <Text strong style={{ fontSize: "13px" }}>{money(bal, cur)}</Text>;
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ height: "70vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Spin size="large" tip="Loading guests..." />
      </div>
    );
  }

  const drawerWidth = drawerFull || isMobile ? "100%" : 860;

  const g = selectedGuest || {};
  const fullName = `${pick(g, "firstName", "FirstName") ?? ""} ${pick(g, "lastName", "LastName") ?? ""}`.trim();
  const idNumber = pick(g, "idNumber", "IdNumber");
  const dob = pick(g, "dateOfBirth", "DateOfBirth", "dob");
  const email = pick(g, "email", "Email");
  const mobile = pick(g, "mobileNumber", "MobileNumber");
  const phone = pick(g, "phoneNumber", "PhoneNumber");
  const companyName = pick(g, "companyName", "CompanyName", "company", "Company");
  const companyEmail = pick(g, "companyEmail", "CompanyEmail");
  const postalAddress = pick(g, "postalAddress", "PostalAddress");
  const residentialAddress = pick(g, "residentialAddress", "ResidentialAddress");
  const legacyAddress = pick(g, "address", "Address");
  const notes = pick(g, "notes", "Notes");
  const remarks = pick(g, "remarks", "Remarks");

  return (
    <div className="hms-page guest-history-page gl-page">
      <PageHeader
        actions={
          <>
            <Button
              icon={<IconInfo />}
              onClick={() =>
                Modal.info({
                  title: "Guests",
                  centered: true,
                  content: (
                    <div>
                      <p>Browse all registered guests.</p>
                      <p>
                        Use <b>Guest details</b> for the profile and <b>Booking history</b> for past stays.
                      </p>
                    </div>
                  ),
                })
              }
            >
              Guide
            </Button>
            <Button icon={<IconRefresh />} onClick={() => fetchGuests(authData.hotelId)} loading={loading}>
              Refresh
            </Button>
          </>
        }
      />

      <div className="hms-card is-flush">
        <div className="gl-filters">
          <Input
            className="gl-search"
            placeholder="Search by name, email or ID"
            prefix={<IconSearch />}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            allowClear
          />
        </div>

        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            style={{ margin: 16 }}
            action={
              <Button size="small" type="primary" onClick={() => fetchGuests(authData.hotelId)}>
                Retry
              </Button>
            }
          />
        )}

        {filteredGuests.length === 0 ? (
          <div className="hms-empty">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No guests found" />
          </div>
        ) : (
          <Table
            className="hms-table"
            columns={guestColumns}
            dataSource={filteredGuests}
            rowKey="guestId"
            scroll={{ x: 1000 }}
            pagination={tablePagination("guest", {
              current: currentPage,
              pageSize: itemsPerPage,
              total: totalEntries,
              onChange: (p, size) => {
                if (size !== itemsPerPage) {
                  setItemsPerPage(size);
                  setCurrentPage(1);
                } else {
                  setCurrentPage(p);
                }
              },
            })}
          />
        )}
      </div>

      {/* Drawer: Mode-based (Details OR History) */}
      <Drawer
        title={drawerMode === "history" ? "Booking history" : "Guest details"}
        placement="right"
        width={drawerWidth}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerFull(false);
          setDrawerLoading(false);
          setBookings([]);
        }}
        open={drawerOpen}
        destroyOnHidden
        extra={
          <Space>
            {/* Quick switch (optional, still keeps separate actions in table) */}
            {drawerMode === "details" ? (
              <Tooltip title="Go to booking history">
                <Button icon={<IconDoc />} onClick={() => openBookingHistory(selectedGuest)}>
                  History
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title="Go to guest details">
                <Button icon={<IconView />} onClick={() => openGuestDetails(selectedGuest)}>
                  Details
                </Button>
              </Tooltip>
            )}

            <Tooltip title={drawerFull ? "Exit full screen" : "Full screen"}>
              <Button
                type="text"
                size="small"
                icon={drawerFull ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={() => setDrawerFull((v) => !v)}
              />
            </Tooltip>
          </Space>
        }
        styles={{ content: { maxWidth: "100vw" } }}
      >
        {drawerLoading ? (
          <Spin tip={drawerMode === "history" ? "Loading history..." : "Loading guest..."} />
        ) : selectedGuest ? (
          drawerMode === "history" ? (
            <>
              <div className="hms-summary">
                <div>
                  <div className="hms-summary-title">{fullName || "—"}</div>
                  <div className="hms-summary-sub">
                    {idNumber || "No ID number"}
                    {email ? ` · ${email}` : ""}
                  </div>
                </div>
              </div>

              {bookings?.length ? (
                <Table
                  className="hms-table"
                  rowKey={(r) => r.bookingId ?? r.bookingNo ?? `${r.checkInDate}-${r.room?.roomNumber}`}
                  columns={bookingColumns}
                  dataSource={bookings}
                  scroll={{ x: true, y: drawerFull || isMobile ? "calc(100vh - 330px)" : 360 }}
                  pagination={tablePagination("booking")}
                />
              ) : (
                <Empty description="No bookings found for this guest" />
              )}
            </>
          ) : (
            <>
              <div className="hms-summary">
                <div>
                  <div className="hms-summary-title">{fullName || "—"}</div>
                  <div className="hms-summary-sub">Guest #{pick(g, "guestId", "GuestId") ?? "—"}</div>
                </div>
                <Button icon={<IconDoc />} onClick={() => openBookingHistory(selectedGuest)}>
                  Booking history
                </Button>
              </div>

              <Section title="Identity">
                <DetailList
                  items={[
                    { label: "ID / passport", value: idNumber },
                    { label: "Date of birth", value: dob ? fmtDate(dob) : null },
                  ]}
                />
              </Section>

              <Section title="Contact">
                <DetailList
                  items={[
                    { label: "Email", value: email },
                    { label: "Mobile", value: mobile },
                    { label: "Phone", value: phone },
                  ]}
                />
              </Section>

              <Section title="Address">
                <DetailList
                  items={[
                    { label: "Postal", value: postalAddress || legacyAddress },
                    { label: "Residential", value: residentialAddress || legacyAddress },
                  ]}
                />
              </Section>

              {(companyName || companyEmail) && (
                <Section title="Company">
                  <DetailList
                    items={[
                      { label: "Company", value: companyName },
                      { label: "Company email", value: companyEmail },
                    ]}
                  />
                </Section>
              )}

              {(remarks || notes) && (
                <Section title="Notes">
                  <DetailList
                    items={[
                      remarks ? { label: "Remarks", value: remarks } : null,
                      notes ? { label: "Notes", value: notes } : null,
                    ]}
                  />
                </Section>
              )}
            </>
          )
        ) : null}
      </Drawer>

      {/* Edit Guest (still placeholder) */}
      <Modal
        open={isEditGuestModalOpen}
        title="Edit guest"
        onCancel={() => setIsEditGuestModalOpen(false)}
        footer={null}
        width={540}
        centered
      >
        <Form
          layout="vertical"
          onFinish={() => {
            notify.success({ message: "Guest updated" });
            setIsEditGuestModalOpen(false);
            fetchGuests(authData.hotelId);
          }}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="email" label="Email" rules={[{ type: "email" }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="mobileNumber" label="Mobile">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="postalAddress" label="Postal Address">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="residentialAddress" label="Residential Address">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>

          <Space>
            <Button onClick={() => setIsEditGuestModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Update
            </Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default GuestListPage;
